import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../../styles/FilePreviewModal.css';

const FilePreviewModal = ({ isOpen, onClose, attachment, fileTypes }) => {
  const [fileType, setFileType] = useState('loading');
  const [fileData, setFileData] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
console.log(attachment, fileTypes)
  // Process attachment and fileTypes when the modal opens or attachment changes
  useEffect(() => {
    if (isOpen && (attachment || fileTypes)) {
      setFileType('loading');
      setErrorMessage('');
      processAttachment();
    }
  }, [isOpen, attachment, fileTypes]);

  const getFileExtension = (filename) => {
    if (!filename) return '';
    // Convert to lowercase and get the part after the last dot
    return filename.toLowerCase().split('.').pop();
  };

  const processAttachment = () => {
    try {
      // Handle the case where fileTypes contains the filename and attachment contains base64 data
      if (fileTypes && attachment) {
        const fileName = typeof fileTypes === 'string' ? fileTypes : 
                       (Array.isArray(fileTypes) && fileTypes.length > 0) ? fileTypes[0] : '';
        
        if (!fileName) {
          setErrorMessage('Filename is missing');
          setFileType('error');
          return;
        }

        const ext = getFileExtension(fileName);
        let base64Data = attachment;
        
        // Clean base64 data if it's a string
        if (typeof base64Data === 'string') {
          // Remove whitespace
          base64Data = base64Data.replace(/\s+/g, '');
          
          // Remove data URL prefix if present
          const dataUrlPrefix = /^data:[^;]+;base64,/;
          if (dataUrlPrefix.test(base64Data)) {
            base64Data = base64Data.replace(dataUrlPrefix, '');
          }
        }
        
        handleFileTypeByExtension(ext, base64Data, fileName);
        return;
      }
      
      // Fallback to original approach if the above doesn't apply
      if (!attachment) {
        setErrorMessage('Attachment data is missing');
        setFileType('error');
        return;
      }

      // Handle string attachment (filename only)
      if (typeof attachment === 'string') {
        const ext = getFileExtension(attachment);
        handleFileTypeByExtension(ext, null, attachment);
        return;
      }

      // Handle object attachment with src property
      if (attachment.src) {
        let cleanBase64 = attachment.src;
        if (typeof cleanBase64 === 'string') {
          cleanBase64 = cleanBase64.replace(/\s+/g, '');
          
          const dataUrlPrefix = /^data:[^;]+;base64,/;
          if (dataUrlPrefix.test(cleanBase64)) {
            cleanBase64 = cleanBase64.replace(dataUrlPrefix, '');
          }
        }

        const fileName = attachment.name || '';
        const ext = getFileExtension(fileName);
        
        handleFileTypeByExtension(ext, cleanBase64, fileName);
        return;
      }
      
      setErrorMessage('Unable to process attachment: invalid format');
      setFileType('error');
    } catch (error) {
      console.error('Error processing attachment for preview:', error);
      setErrorMessage('Failed to prepare file for preview: ' + error.message);
      setFileType('error');
    }
  };

  const handleFileTypeByExtension = (extension, base64Data, fileName) => {
    let type = 'unsupported';
    let dataUrl = '';
    let error = '';

    switch (extension) {
      case 'pdf':
        type = 'pdf';
        dataUrl = base64Data ? `data:application/pdf;base64,${base64Data}` : '';
        break;
      case 'jpg':
      case 'jpeg':
        type = 'image';
        dataUrl = base64Data ? `data:image/jpeg;base64,${base64Data}` : '';
        break;
      case 'png':
        type = 'image';
        dataUrl = base64Data ? `data:image/png;base64,${base64Data}` : '';
        break;
      case 'gif':
        type = 'image';
        dataUrl = base64Data ? `data:image/gif;base64,${base64Data}` : '';
        break;
      case 'doc':
      case 'docx':
        type = 'unsupported';
        dataUrl = base64Data ? `data:application/octet-stream;base64,${base64Data}` : '';
        error = 'Word documents cannot be previewed directly. Please download the file.';
        break;
      case 'xls':
      case 'xlsx':
        type = 'unsupported';
        dataUrl = base64Data ? `data:application/octet-stream;base64,${base64Data}` : '';
        error = 'Excel files cannot be previewed directly. Please download the file.';
        break;
      case 'html':
      case 'htm':
      case 'svg':
      case 'js':
      case 'exe':
      case 'php':
        type = 'unsupported';
        dataUrl = '';
        error = 'This file type cannot be previewed for security reasons.';
        break;
      default:
        // Still allow download when content exists (e.g. filename without extension)
        type = 'unsupported';
        dataUrl = base64Data
          ? `data:application/octet-stream;base64,${base64Data}`
          : '';
        error =
          'This file type cannot be previewed directly. Please download the file.';
    }

    setFileType(type);
    setFileData(dataUrl);
    if (error) setErrorMessage(error);
  };

  const getRawBase64 = () => {
    if (typeof attachment === 'string' && attachment.length > 100) {
      return attachment.replace(/\s+/g, '').replace(/^data:[^;]+;base64,/, '');
    }
    if (attachment && attachment.src) {
      return String(attachment.src)
        .replace(/\s+/g, '')
        .replace(/^data:[^;]+;base64,/, '');
    }
    return '';
  };

  const canDownload = Boolean(
    fileData || getRawBase64()
  );

  const handleDownload = () => {
    let href = fileData;
    if (!href) {
      const raw = getRawBase64();
      if (!raw) {
        setErrorMessage('No file data available for download');
        return;
      }
      href = `data:application/octet-stream;base64,${raw}`;
    }
    
    try {
      const link = document.createElement('a');
      link.href = href;
      
      // Determine download filename
      let downloadName = 'download';
      if (typeof fileTypes === 'string') {
        downloadName = fileTypes;
      } else if (Array.isArray(fileTypes) && fileTypes.length > 0) {
        downloadName = fileTypes[0];
      } else if (typeof attachment === 'string' && attachment.length < 200) {
        downloadName = attachment;
      } else if (attachment && attachment.name) {
        downloadName = attachment.name;
      }
      
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      setErrorMessage('Failed to download file: ' + error.message);
    }
  };

  // Helper function to get filename for display
  const getFileName = () => {
    if (typeof fileTypes === 'string') {
      return fileTypes;
    } else if (Array.isArray(fileTypes) && fileTypes.length > 0) {
      return fileTypes[0];
    } else if (typeof attachment === 'string') {
      return attachment;
    } else if (attachment && attachment.name) {
      return attachment.name;
    }
    return 'File Preview';
  };

  if (!isOpen) return null;

  return (
    <div className="file-preview-modal">
      <div className="file-preview-backdrop" onClick={onClose}></div>
      <div className="file-preview-container">
        <div className="file-preview-header">
          <h3>
            <i className="bi bi-file-earmark"></i>
            {getFileName()}
          </h3>
          <div className="file-preview-actions">
            {canDownload && (
              <button className="preview-action-btn" onClick={handleDownload} title="Download">
                <i className="bi bi-download"></i>
              </button>
            )}
            <button className="preview-action-btn" onClick={onClose} title="Close">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <div className="file-preview-content">
          {fileType === 'loading' && (
            <div className="file-preview-loading">
              <i className="bi bi-arrow-repeat spinning"></i>
              <p>Loading preview...</p>
            </div>
          )}

          {fileType === 'error' && (
            <div className="file-preview-error">
              <i className="bi bi-exclamation-triangle"></i>
              <p>{errorMessage || 'Error loading file preview'}</p>
              <button className="button secondary" onClick={onClose}>Close</button>
            </div>
          )}

          {fileType === 'unsupported' && (
            <div className="file-preview-unsupported">
              <i className="bi bi-file-earmark-x"></i>
              <p>{errorMessage}</p>
              {canDownload && (
                <button className="button primary" onClick={handleDownload}>
                  <i className="bi bi-download"></i> Download File
                </button>
              )}
            </div>
          )}

          {fileType === 'pdf' && fileData && fileData.startsWith('data:application/pdf') && (
            <iframe 
              src={fileData} 
              className="pdf-preview" 
              title={getFileName()}
              width="100%" 
              height="100%"
            ></iframe>
          )}

          {fileType === 'image' && fileData && fileData.startsWith('data:image/') && !fileData.startsWith('data:image/svg') && (
            <div className="image-preview-wrapper">
              <img 
                src={fileData} 
                alt={getFileName()} 
                className="image-preview"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

FilePreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attachment: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      name: PropTypes.string,
      src: PropTypes.string,
      desc: PropTypes.string,
      uploadDate: PropTypes.string
    })
  ]),
  fileTypes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array
  ])
};

export default FilePreviewModal;